/**
 * Centralized env access. Surfaces a clear error early in dev if anything is
 * missing instead of a deep `undefined` later in an axios call.
 *
 * In `vite dev`, HTTP and WS use same-origin URLs so requests hit
 * `vite.config.ts` proxies → backend. That avoids CORS and localhost/127.0.0.1
 * mismatches when the app is opened as `http://127.0.0.1:5173`.
 */
function required(key: string, hint: string): string {
  const raw = import.meta.env[key] as string | undefined;
  if (!raw?.trim()) {
    throw new Error(`Missing required env var: ${key}. ${hint}`);
  }
  return raw.replace(/\/+$/, '');
}

function devWsBase(): string {
  if (typeof window === 'undefined') {
    return 'ws://127.0.0.1:5173';
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}`;
}

const prodHint =
  'For production builds, copy .env.production.example to .env.production and set HTTPS/WSS URLs for your Django host (SPA may be on a different VPS).';

export const env = {
  apiBase: import.meta.env.DEV
    ? ''
    : required('VITE_API_BASE_URL', prodHint),
  wsBase: import.meta.env.DEV ? devWsBase() : required('VITE_WS_BASE_URL', prodHint),
  // The geo API (3D building map) is served by the same backend as this SPA
  // (see backend/app/main.py), so `/geo` is always same-origin in production
  // too. VITE_GEO_API_BASE_URL is only needed to point at a different host.
  //
  // In dev, VITE_GEO_API_BASE_URL is consumed by vite.config.ts as the proxy
  // *target* for the `/geo` path (e.g. `http://127.0.0.1:8100`, no `/geo`
  // suffix) — it must NOT be used here too, or requests skip the proxy and
  // land on the bare host without the `/geo` prefix the backend expects.
  geoApiBase: import.meta.env.DEV
    ? '/geo'
    : (import.meta.env.VITE_GEO_API_BASE_URL as string | undefined)?.replace(/\/+$/, '') || '/geo',
  /**
   * Self-hosted terrain-RGB DEM tile template ({z}/{x}/{y} placeholders).
   * Optional: when unset, all 3D-terrain features (terrain, hillshade,
   * contours, elevation-aware overlays) silently disable and maps stay flat.
   * See scripts/terrain/README.md.
   */
  terrainTilesUrl: (import.meta.env.VITE_TERRAIN_TILES_URL ?? '').trim(),
  isDev: import.meta.env.DEV,
};
